import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABEL = "block text-lg font-medium text-stone-900 mb-2";
const FIELD =
  "h-12 w-full rounded-xl border-2 border-stone-300 bg-white px-4 text-lg text-stone-900 focus-visible:border-orange-600 placeholder:text-stone-500";

const EMPTY = { name: "", preferred_contact: "", contact_detail: "", message: "", consent: false };

export const CareersApplyForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!form.preferred_contact) er.preferred_contact = "Please choose a contact method.";
    if (!form.contact_detail.trim()) er.contact_detail = "Please enter an email or phone number.";
    if (!form.message.trim()) er.message = "Please tell us a little about yourself.";
    if (!form.consent) er.consent = "Please confirm you have read the Privacy Policy.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please check the form and try again.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, {
        ...form,
        relationship: "Applicant",
        enquiry_type: "Careers application",
      });
      setSent(true);
      toast.success("Your application has been sent. Thank you!");
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please email us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const ErrorText = ({ id, children }) =>
    children ? (
      <p id={id} className="mt-2 text-base text-red-700 font-medium" role="alert">
        {children}
      </p>
    ) : null;

  if (sent) {
    return (
      <div className="rounded-2xl border-2 border-green-200 bg-green-50 p-8 text-center" data-testid="careers-apply-success" role="status">
        <CheckCircle2 className="mx-auto h-12 w-12 text-green-700" aria-hidden="true" />
        <h3 className="mt-4 font-heading text-2xl font-semibold text-stone-900">Application sent</h3>
        <p className="mt-2 text-lg text-stone-700">
          Thank you for your interest in joining Spire Inclusion. We will be in touch soon.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-5" data-testid="careers-apply-form">
      <div>
        <label htmlFor="apply-name" className={LABEL}>Your name</label>
        <input id="apply-name" type="text" autoComplete="name" value={form.name} onChange={set("name")} aria-required="true" aria-invalid={!!errors.name} aria-describedby={errors.name ? "apply-name-error" : undefined} className={FIELD} data-testid="careers-apply-name-input" />
        <ErrorText id="apply-name-error">{errors.name}</ErrorText>
      </div>

      <div>
        <label htmlFor="apply-contact-method" className={LABEL}>Preferred contact method</label>
        <select id="apply-contact-method" value={form.preferred_contact} onChange={set("preferred_contact")} aria-required="true" aria-invalid={!!errors.preferred_contact} aria-describedby={errors.preferred_contact ? "apply-method-error" : undefined} className={FIELD} data-testid="careers-apply-method-select">
          <option value="">Please choose…</option>
          {["Email", "Phone", "Either"].map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <ErrorText id="apply-method-error">{errors.preferred_contact}</ErrorText>
      </div>

      <div>
        <label htmlFor="apply-contact" className={LABEL}>Email address or phone number</label>
        <input id="apply-contact" type="text" value={form.contact_detail} onChange={set("contact_detail")} aria-required="true" aria-invalid={!!errors.contact_detail} aria-describedby={errors.contact_detail ? "apply-contact-error" : undefined} className={FIELD} data-testid="careers-apply-contact-input" />
        <ErrorText id="apply-contact-error">{errors.contact_detail}</ErrorText>
      </div>

      <div>
        <label htmlFor="apply-message" className={LABEL}>Tell us a little about yourself</label>
        <textarea
          id="apply-message"
          rows={5}
          value={form.message}
          onChange={set("message")}
          placeholder="A few lines about you, any relevant experience, and why you'd like to work with us. No CV needed at this stage."
          aria-required="true"
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? "apply-message-error" : undefined}
          className="w-full rounded-xl border-2 border-stone-300 bg-white px-4 py-3 text-lg text-stone-900 focus-visible:border-orange-600 placeholder:text-stone-500"
          data-testid="careers-apply-message-input"
        />
        <ErrorText id="apply-message-error">{errors.message}</ErrorText>
      </div>

      <div>
        <div className="flex items-start gap-3">
          <input id="apply-consent" type="checkbox" checked={form.consent} onChange={set("consent")} aria-required="true" aria-invalid={!!errors.consent} aria-describedby={errors.consent ? "apply-consent-error" : undefined} className="mt-1.5 h-6 w-6 shrink-0 rounded border-2 border-stone-400 text-orange-600 accent-orange-600" data-testid="careers-apply-consent-checkbox" />
          <label htmlFor="apply-consent" className="text-lg text-stone-800 leading-relaxed">
            I have read the{" "}
            <Link to="/privacy-policy" className="font-semibold text-orange-700 underline">Privacy Policy</Link>{" "}
            and understand how Spire Inclusion will use my details.
          </label>
        </div>
        <ErrorText id="apply-consent-error">{errors.consent}</ErrorText>
      </div>

      <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-orange-600 text-white font-semibold text-lg hover:bg-orange-700 transition-colors disabled:opacity-70" data-testid="careers-apply-submit-button">
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Send className="h-5 w-5" aria-hidden="true" />}
        {submitting ? "Sending…" : "Send application"}
      </button>
    </form>
  );
};
